// ===================================================================
// Intake -- Property (Lost & Found)
// Full-featured property management: log, track, reunite items
// Ported from Property.jsx with photo capture, AI describe, reunite
// ===================================================================

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  Select, Textarea, Modal, SimpleGrid, SegmentedControl, Center, ThemeIcon,
  Image, FileButton, ActionIcon, Checkbox, Tooltip, Box, Divider, Pagination,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconPackage, IconPlus, IconSearch, IconAlertTriangle, IconCheck,
  IconCamera, IconTrash, IconPhoto, IconSparkles, IconPrinter,
  IconHandGrab, IconUser,
} from '@tabler/icons-react';

const PROP_PAGE_SIZE = 25;
import { useIntakeStore, type PropertyItem } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import {
  PROPERTY_CATEGORIES, formatDateTime, generateItemCode, resizeImageToBase64, resizeBase64ToThumb,
} from './lib/helpers';
import jsPDF from 'jspdf';
import { WORKER_URL } from '../../lib/api-switch';
const ORIGIN = 'https://apps.opengov.com';

// ── Status colors ────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  logged: 'var(--color-primary-700)',
  stored: '#C68700',
  released: 'var(--color-success)',
  reunited: 'var(--color-success)',
};

// ── Category icon map (Tabler fallback since we can't use FA) ────
const CATEGORY_LABELS: Record<string, string> = {};
PROPERTY_CATEGORIES.forEach(c => { CATEGORY_LABELS[c.key] = c.label; });

export default function PropertyPage() {
  const {
    propertyItems, individuals,
    loadPropertyItems, loadIndividuals,
    createPropertyItem, updatePropertyItem, deletePropertyItem,
  } = useIntakeStore();
  const { activeIncident, user } = useAuthStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propPage, setPropPage] = useState(1);

  // Log item modal
  const [addOpen, setAddOpen] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newStorage, setNewStorage] = useState('');
  const [newQuantity, setNewQuantity] = useState('1');
  const [newIndividualId, setNewIndividualId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newPhoto, setNewPhoto] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false); // synchronous guard against rapid double-clicks
  const [aiDescribing, setAiDescribing] = useState(false);

  // Camera state
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  // Detail modal
  const [detailItem, setDetailItem] = useState<PropertyItem | null>(null);
  const [deleteItemConfirm, setDeleteItemConfirm] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);

  // Reunite modal
  const [reuniteItem, setReuniteItem] = useState<PropertyItem | null>(null);
  const [reuniteOwnerId, setReuniteOwnerId] = useState('');
  const [reuniteNotes, setReuniteNotes] = useState('');
  const [reuniteRefused, setReuniteRefused] = useState(false);
  const [reuniting, setReuniting] = useState(false);

  useEffect(() => {
    if (activeIncident?.id) {
      loadPropertyItems(String(activeIncident.id));
      loadIndividuals();
    }
  }, [activeIncident?.id, loadPropertyItems, loadIndividuals]);

  // ── Filtered data ──────────────────────────────────────────────
  // Store already filters by active incident — use directly
  const incidentItems = activeIncident ? propertyItems : [];

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { logged: 0, stored: 0, released: 0, reunited: 0 };
    incidentItems.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return counts;
  }, [incidentItems]);

  const filtered = useMemo(() => {
    let list = incidentItems;
    if (statusFilter === 'unclaimed') {
      list = list.filter(p => p.status === 'logged' || p.status === 'stored');
    } else if (statusFilter === 'reunited') {
      list = list.filter(p => p.status === 'released' || p.status === 'reunited');
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.description || '').toLowerCase().includes(q) ||
        (p.storageLocation || '').toLowerCase().includes(q) ||
        (p.receivedBy || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || ''));
  }, [incidentItems, statusFilter, search]);

  useEffect(() => { setPropPage(1); }, [search, statusFilter]);
  const propTotalPages = Math.max(1, Math.ceil(filtered.length / PROP_PAGE_SIZE));
  const pagedProps = filtered.slice((propPage - 1) * PROP_PAGE_SIZE, propPage * PROP_PAGE_SIZE);

  const individualOptions = useMemo(() => {
    if (!activeIncident) return [];
    return individuals
      .filter(i => String(i.incidentId) === String(activeIncident.id))
      .map(i => ({ value: String(i.id), label: `${i.lastName}, ${i.firstName} (${i.tagCode})` }));
  }, [individuals, activeIncident]);

  // ── Camera helpers ─────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      cameraStreamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current && cameraStreamRef.current) {
          videoRef.current.srcObject = cameraStreamRef.current;
        }
      }, 50);
    } catch {
      notifications.show({ title: 'Camera Error', message: 'Could not access camera.', color: 'var(--color-error)' });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setNewPhoto(dataUrl);
    stopCamera();
  };

  const stopCamera = () => {
    cameraStreamRef.current?.getTracks().forEach(t => t.stop());
    cameraStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const handlePhotoFile = async (file: File | null) => {
    if (!file) return;
    try {
      const base64 = await resizeImageToBase64(file, 1200);
      setNewPhoto(base64);
    } catch {
      notifications.show({ title: 'Error', message: 'Could not process image.', color: 'var(--color-error)' });
    }
  };

  // ── AI Describe (direct worker API call for vision) ───────────
  const handleAiDescribe = async () => {
    if (!newPhoto) {
      notifications.show({ title: 'No Photo', message: 'Take or upload a photo first.', color: '#C68700' });
      return;
    }
    setAiDescribing(true);
    try {
      const base64Data = newPhoto.includes(',') ? newPhoto.split(',')[1] : newPhoto;
      const mediaType = newPhoto.startsWith('data:image/png') ? 'image/png' : 'image/jpeg';
      const resp = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Origin': ORIGIN },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 256,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'image',
                source: { type: 'base64', media_type: mediaType, data: base64Data },
              },
              {
                type: 'text',
                text: 'Describe this item concisely for a lost-and-found property log. Include color, brand if visible, size, condition, and distinguishing features. Keep it under 2 sentences.',
              },
            ],
          }],
        }),
      });
      if (!resp.ok) throw new Error(`AI API: ${resp.status}`);
      const data = await resp.json();
      const text = data?.content?.[0]?.text || '';
      if (text) {
        setNewDesc(text.trim());
        notifications.show({ title: 'AI Description', message: 'Description generated from photo.', color: 'var(--color-primary-700)' });
      }
    } catch (err) {
      notifications.show({
        title: 'AI Error',
        message: err instanceof Error ? err.message : 'Failed to describe photo.',
        color: 'var(--color-error)',
      });
    } finally {
      setAiDescribing(false);
    }
  };

  // ── Create item ────────────────────────────────────────────────
  const handleAdd = async () => {
    if (submittingRef.current) return;
    if (!newDesc.trim()) {
      notifications.show({ title: 'Required', message: 'Description is required.', color: 'var(--color-error)' });
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const itemCode = generateItemCode();
      // Downsize photo to thumbnail before storing — Ignatius TEXT field is ~65KB
      const thumb = newPhoto ? await resizeBase64ToThumb(newPhoto, 400, 0.55) : '';
      await createPropertyItem({
        incidentId: String(activeIncident?.id || ''),
        description: `[${itemCode}] ${newCategory ? `[${newCategory}] ` : ''}${newDesc.trim()}${newNotes ? ` | Notes: ${newNotes}` : ''}`,
        quantity: newQuantity || '1',
        storageLocation: newStorage,
        status: 'logged',
        individualId: newIndividualId || '',
        receivedBy: user?.fullName || '',
        photoData: thumb,
      });
      notifications.show({ title: 'Item Logged', message: `Property item ${itemCode} has been recorded.`, color: 'var(--color-success)' });
      resetAddForm();
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to log item.', color: 'var(--color-error)' });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const resetAddForm = () => {
    setAddOpen(false);
    setNewDesc('');
    setNewCategory('');
    setNewStorage('');
    setNewQuantity('1');
    setNewIndividualId('');
    setNewNotes('');
    setNewPhoto('');
    stopCamera();
  };

  // ── Reunite item ───────────────────────────────────────────────
  const handleReunite = async () => {
    if (!reuniteItem) return;
    setReuniting(true);
    try {
      const notes = reuniteRefused
        ? `REFUSED RETURN. ${reuniteNotes}`.trim()
        : reuniteNotes.trim();
      await updatePropertyItem(reuniteItem.id, {
        status: 'released',
        individualId: reuniteOwnerId || reuniteItem.individualId,
        description: reuniteItem.description + (notes ? ` | Reunite: ${notes}` : ''),
      });
      notifications.show({ title: 'Reunited', message: 'Item has been marked as returned.', color: 'var(--color-success)' });
      setReuniteItem(null);
      setReuniteOwnerId('');
      setReuniteNotes('');
      setReuniteRefused(false);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to reunite.', color: 'var(--color-error)' });
    } finally {
      setReuniting(false);
    }
  };

  // ── Code 128B barcode encoder ──────────────────────────────────
  const CODE128B_START = 104;
  const CODE128_STOP  = 106;
  const CODE128_PATTERNS = [
    '11011001100','11001101100','11001100110','10010011000','10010001100',
    '10001001100','10011001000','10011000100','10001100100','11001001000',
    '11001000100','11000100100','10110011100','10011011100','10011001110',
    '10111001100','10011101100','10011100110','11001110010','11001011100',
    '11001001110','11011100100','11001110100','11100101100','11100100110',
    '11101100100','11100110100','11100110010','11011011000','11011000110',
    '11000110110','10100011000','10001011000','10001000110','10110001000',
    '10001101000','10001100010','11010001000','11000101000','11000100010',
    '10110111000','10110001110','10001101110','10111011000','10111000110',
    '10001110110','11101110110','11010001110','11000101110','11011101000',
    '11011100010','11011101110','11101011000','11101000110','11100010110',
    '11101101000','11101100010','11100011010','11101111010','11001000010',
    '11110001010','10100110000','10100001100','10010110000','10010000110',
    '10000101100','10000100110','10110010000','10110000100','10011010000',
    '10011000010','10000110100','10000110010','11000010010','11001010000',
    '11110111010','11000010100','10001111010','10100111100','10010111100',
    '10010011110','10111100100','10011110100','10011110010','11110100100',
    '11110010100','11110010010','11011011110','11011110110','11110110110',
    '10101111000','10100011110','10001011110','10111101000','10111100010',
    '11110101000','11110100010','10111011110','10111101110','11101011110',
    '11110101110','11010000100','11010010000','11010011100','1100011101011',
  ];

  function encodeCode128B(text: string) {
    const codes = [CODE128B_START];
    for (let i = 0; i < text.length; i++) codes.push(text.charCodeAt(i) - 32);
    let checksum = codes[0];
    for (let i = 1; i < codes.length; i++) checksum += codes[i] * i;
    codes.push(checksum % 103);
    codes.push(CODE128_STOP);
    return codes.map(c => CODE128_PATTERNS[c]).join('');
  }

  function drawBarcode(pdf: jsPDF, text: string, x: number, y: number, width: number, height: number) {
    const pattern = encodeCode128B(text);
    const barWidth = width / pattern.length;
    pdf.setFillColor(17, 45, 78);
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] === '1') pdf.rect(x + i * barWidth, y, barWidth, height, 'F');
    }
  }

  // ── Print Avery label (jsPDF, 5163 format) ───────────────────
  const handlePrintLabel = (item: PropertyItem) => {
    const codeMatch = item.description.match(/\[IU-P-[A-Z0-9]+\]/);
    const code = codeMatch ? codeMatch[0].replace(/[[\]]/g, '') : `ITEM-${item.id}`;
    const owner = item.individualId
      ? individuals.find(i => String(i.id) === item.individualId)
      : null;
    const ownerStr = owner ? `${owner.lastName}, ${owner.firstName}` : 'Unknown';
    const cleanDesc = item.description.replace(/\[.*?\]\s*/g, '').split(' | Notes:')[0].split(' | Reunite:')[0];
    const catMatch = item.description.match(/\[([a-z]+)\]/i);
    const category = catMatch ? (CATEGORY_LABELS[catMatch[1]] || catMatch[1]) : '';

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const cols = 2, rows = 5;
    const labelW = 101.6, labelH = 50.8;
    const marginX = (pageW - cols * labelW) / 2;
    const marginY = (pageH - rows * labelH) / 2;
    const navy: [number, number, number] = [17, 45, 78];
    const labelsPerPage = cols * rows;

    // Fill page with copies of this label
    for (let idx = 0; idx < labelsPerPage; idx++) {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = marginX + col * labelW;
      const y = marginY + row * labelH;

      // Cut guide border
      pdf.setDrawColor(210, 210, 210);
      pdf.setLineWidth(0.2);
      pdf.rect(x, y, labelW, labelH);

      // Blue strip on left
      pdf.setFillColor(44, 1, 214);
      pdf.rect(x, y, 4, labelH, 'F');

      // Item code (large)
      pdf.setFont('courier', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...navy);
      pdf.text(code, x + 10, y + 10);

      // Barcode
      drawBarcode(pdf, code, x + 10, y + 13, 55, 9);

      // Category
      if (category) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(44, 1, 214);
        pdf.text(category.toUpperCase(), x + 10, y + 28);
      }

      // Description (truncated)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...navy);
      const descLines = pdf.splitTextToSize(cleanDesc, labelW - 20);
      pdf.text(descLines.slice(0, 2), x + 10, y + 33);

      // Owner + Location
      pdf.setFontSize(7.5);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Owner: ${ownerStr}`, x + 10, y + 42);
      pdf.text(`Location: ${item.storageLocation || 'N/A'}`, x + 10, y + 46);

      // Branding
      pdf.setFontSize(6);
      pdf.setTextColor(148, 163, 184);
      pdf.text('OpenGov EMC', x + labelW - 6, y + labelH - 3, { align: 'right' });
    }

    pdf.save(`property-label-${code}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ── No incident guard ──────────────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="#939598"><IconAlertTriangle size={30} /></ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Use the incident selector in the top header bar.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Property</Title>
          <Badge size="sm" variant="light">{incidentItems.length} items</Badge>
        </Group>
        <Button leftSection={<IconPlus size={14} />} size="sm" onClick={() => setAddOpen(true)}>
          Log Item
        </Button>
      </Group>

      {/* Filter Tabs */}
      <Paper p="md" radius="sm" withBorder mb="md">
        <Group gap="md">
          <TextInput
            placeholder="Search items..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <SegmentedControl
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: 'all', label: `All Items (${incidentItems.length})` },
              { value: 'unclaimed', label: `Unclaimed (${statusCounts.logged + statusCounts.stored})` },
              { value: 'reunited', label: `Reunited (${statusCounts.released + statusCounts.reunited})` },
            ]}
            size="xs"
          />
        </Group>
      </Paper>

      {/* Items Grid */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder ta="center">
          <Text c="dimmed">No property items found.</Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          {pagedProps.map(item => {
            const owner = item.individualId
              ? individuals.find(i => String(i.id) === item.individualId)
              : null;
            const codeMatch = item.description.match(/\[IU-P-[A-Z0-9]+\]/);
            const itemCode = codeMatch ? codeMatch[0].replace(/[[\]]/g, '') : '';
            const catMatch = item.description.match(/\[([a-z]+)\]/i);
            const category = catMatch ? catMatch[1] : '';
            const cleanDesc = item.description.replace(/\[.*?\]\s*/g, '').split(' | Notes:')[0].split(' | Reunite:')[0];

            return (
              <Paper
                key={item.id}
                p="md"
                radius="sm"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => setDetailItem(item)}
              >
                {/* Item photo or icon fallback */}
                {item.photoData ? (
                  <Image src={item.photoData} h={80} fit="cover" radius={6} mb="xs" />
                ) : (
                  <Box mb="xs" style={{
                    height: 60, borderRadius: 4,
                    background: '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <IconPackage size={28} color="#939598" />
                  </Box>
                )}

                <Group justify="space-between" mb={4}>
                  {itemCode && (
                    <Badge size="xs" variant="outline" color="#939598" ff="monospace">{itemCode}</Badge>
                  )}
                  <Badge size="sm" color={STATUS_COLORS[item.status] || '#939598'}>{item.status}</Badge>
                </Group>

                {category && (
                  <Badge size="xs" variant="light" color="var(--color-primary-700)" mb={4}>
                    {CATEGORY_LABELS[category] || category}
                  </Badge>
                )}

                <Text size="sm" fw={500} lineClamp={2}>{cleanDesc}</Text>

                {owner ? (
                  <Group gap={4} mt="xs">
                    <IconUser size={12} />
                    <Text size="xs" c="dimmed">{owner.lastName}, {owner.firstName}</Text>
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed" mt="xs">Unknown owner</Text>
                )}

                <Text size="xs" c="dimmed" mt={4}>{formatDateTime(item.datecreated)}</Text>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {propTotalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={propTotalPages} value={propPage} onChange={setPropPage} size="sm" />
        </Group>
      )}

      {/* ── Detail Modal ──────────────────────────────────────── */}
      <Modal
        opened={!!detailItem}
        onClose={() => { setDetailItem(null); setDeleteItemConfirm(false); }}
        title="Item Details"
        size="lg"
        centered
      >
        {detailItem && (() => {
          const owner = detailItem.individualId
            ? individuals.find(i => String(i.id) === detailItem.individualId)
            : null;
          const cleanDesc = detailItem.description.replace(/\[.*?\]\s*/g, '').split(' | Notes:')[0].split(' | Reunite:')[0];
          const notesMatch = detailItem.description.match(/\| Notes: (.+?)(\| Reunite:|$)/);
          const itemNotes = notesMatch ? notesMatch[1].trim() : '';

          return (
            <Stack gap="md">
              <Group justify="space-between">
                <Badge size="lg" color={STATUS_COLORS[detailItem.status] || '#939598'}>{detailItem.status}</Badge>
                <Text size="sm" c="dimmed">Qty: {detailItem.quantity || '1'}</Text>
              </Group>

              {detailItem.photoData && (
                <Image src={detailItem.photoData} w="100%" mah={300} fit="contain" radius="sm" />
              )}

              <div>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{cleanDesc}</Text>
              </div>

              {detailItem.storageLocation && (
                <div>
                  <Text size="xs" c="dimmed">Storage Location</Text>
                  <Text size="sm">{detailItem.storageLocation}</Text>
                </div>
              )}

              {owner && (
                <div>
                  <Text size="xs" c="dimmed">Owner</Text>
                  <Text size="sm">{owner.lastName}, {owner.firstName} ({owner.tagCode})</Text>
                </div>
              )}

              {detailItem.receivedBy && (
                <div>
                  <Text size="xs" c="dimmed">Received By</Text>
                  <Text size="sm">{detailItem.receivedBy}</Text>
                </div>
              )}

              {itemNotes && (
                <div>
                  <Text size="xs" c="dimmed">Notes</Text>
                  <Text size="sm">{itemNotes}</Text>
                </div>
              )}

              <div>
                <Text size="xs" c="dimmed">Logged</Text>
                <Text size="sm">{formatDateTime(detailItem.datecreated)}</Text>
              </div>

              <Divider />

              <Group justify="space-between">
                {user?.isAdmin && (
                  deleteItemConfirm ? (
                    <Group gap={4}>
                      <Text size="xs" c="var(--color-error)">Delete this item?</Text>
                      <Button size="xs" color="var(--color-error)" loading={deletingItem} onClick={async () => {
                        setDeletingItem(true);
                        try {
                          await deletePropertyItem(detailItem.id);
                          notifications.show({ title: 'Deleted', message: 'Property item deleted.', color: 'var(--color-error)' });
                          setDetailItem(null);
                          setDeleteItemConfirm(false);
                        } catch {
                          notifications.show({ title: 'Error', message: 'Failed to delete item.', color: 'var(--color-error)' });
                        } finally { setDeletingItem(false); }
                      }}>
                        Confirm Delete
                      </Button>
                      <Button size="xs" variant="subtle" onClick={() => setDeleteItemConfirm(false)}>Cancel</Button>
                    </Group>
                  ) : (
                    <Button
                      variant="subtle"
                      size="sm"
                      color="var(--color-error)"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => setDeleteItemConfirm(true)}
                    >
                      Delete
                    </Button>
                  )
                )}
                <Group gap="xs" style={{ marginLeft: 'auto' }}>
                  <Tooltip label="Print label for this item">
                    <Button
                      variant="light"
                      size="sm"
                      leftSection={<IconPrinter size={14} />}
                      onClick={() => handlePrintLabel(detailItem)}
                    >
                      Print Label
                    </Button>
                  </Tooltip>
                  {detailItem.status !== 'released' && detailItem.status !== 'reunited' && (
                    <Button
                      size="sm"
                      leftSection={<IconHandGrab size={14} />}
                      color="var(--color-success)"
                      onClick={() => {
                        setDetailItem(null);
                        setReuniteItem(detailItem);
                      }}
                    >
                      Reunite
                    </Button>
                  )}
                </Group>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* ── Log Item Modal ────────────────────────────────────── */}
      <Modal
        opened={addOpen}
        onClose={resetAddForm}
        title="Log Property Item"
        size="lg"
        centered
      >
        <Stack gap="md">
          {/* Photo capture */}
          {newPhoto ? (
            <Group>
              <Image src={newPhoto} w={160} h={120} radius="sm" fit="cover" />
              <Stack gap="xs">
                <Button
                  variant="light"
                  color="var(--color-error)"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => setNewPhoto('')}
                >
                  Remove Photo
                </Button>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconSparkles size={14} />}
                  loading={aiDescribing}
                  onClick={handleAiDescribe}
                >
                  AI Describe
                </Button>
              </Stack>
            </Group>
          ) : cameraActive ? (
            <Stack align="center">
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxWidth: 400, borderRadius: 4 }} />
              <Group>
                <Button onClick={capturePhoto} leftSection={<IconCamera size={14} />}>Capture</Button>
                <Button variant="subtle" onClick={stopCamera}>Cancel</Button>
              </Group>
            </Stack>
          ) : (
            <Group>
              <FileButton onChange={handlePhotoFile} accept="image/*">
                {(props) => (
                  <Button variant="light" leftSection={<IconPhoto size={14} />} {...props}>
                    Upload Photo
                  </Button>
                )}
              </FileButton>
              <Button variant="light" leftSection={<IconCamera size={14} />} onClick={startCamera}>
                Use Camera
              </Button>
            </Group>
          )}

          {/* Category selection grid */}
          <div>
            <Text size="sm" fw={500} mb="xs">Category</Text>
            <SimpleGrid cols={{ base: 3, sm: 4 }}>
              {PROPERTY_CATEGORIES.map(cat => (
                <Button
                  key={cat.key}
                  variant={newCategory === cat.key ? 'filled' : 'light'}
                  color={newCategory === cat.key ? 'var(--color-primary-700)' : '#939598'}
                  size="xs"
                  onClick={() => setNewCategory(newCategory === cat.key ? '' : cat.key)}
                  style={{ height: 'auto', padding: '8px 4px' }}
                >
                  <Stack gap={2} align="center">
                    <IconPackage size={16} />
                    <Text size="xs" ta="center" lineClamp={1}>{cat.label}</Text>
                  </Stack>
                </Button>
              ))}
            </SimpleGrid>
          </div>

          <Textarea
            label="Description *"
            placeholder="Describe the item (color, brand, condition)..."
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            minRows={2}
            rightSection={
              newPhoto ? (
                <Tooltip label="Generate description from photo using AI">
                  <ActionIcon
                    variant="subtle"
                    color="var(--color-primary-700)"
                    loading={aiDescribing}
                    onClick={handleAiDescribe}
                  >
                    <IconSparkles size={16} />
                  </ActionIcon>
                </Tooltip>
              ) : undefined
            }
          />

          <TextInput
            label="Location Found"
            placeholder="Where was the item found?"
            value={newStorage}
            onChange={e => setNewStorage(e.target.value)}
          />

          <Select
            label="Link to Owner"
            placeholder="Search individuals..."
            data={individualOptions}
            value={newIndividualId || null}
            onChange={v => setNewIndividualId(v || '')}
            searchable
            clearable
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Quantity"
              type="number"
              min={1}
              value={newQuantity}
              onChange={e => setNewQuantity(e.target.value)}
            />
            <div />
          </SimpleGrid>

          <Textarea
            label="Notes"
            placeholder="Additional notes..."
            value={newNotes}
            onChange={e => setNewNotes(e.target.value)}
            minRows={2}
          />

          <Group justify="flex-end">
            <Button variant="subtle" onClick={resetAddForm}>Cancel</Button>
            <Button onClick={handleAdd} loading={submitting} disabled={submitting || !newDesc.trim()}>
              Log Item
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Reunite Modal ─────────────────────────────────────── */}
      <Modal
        opened={!!reuniteItem}
        onClose={() => { setReuniteItem(null); setReuniteOwnerId(''); setReuniteNotes(''); setReuniteRefused(false); }}
        title="Reunite Property"
        size="md"
        centered
      >
        {reuniteItem && (() => {
          const cleanDesc = reuniteItem.description.replace(/\[.*?\]\s*/g, '').split(' | Notes:')[0];
          const existingOwner = reuniteItem.individualId
            ? individuals.find(i => String(i.id) === reuniteItem.individualId)
            : null;

          return (
            <Stack gap="md">
              <Paper p="sm" radius="sm" withBorder style={{ background: '#F8F8F8' }}>
                <Text size="sm" fw={500}>{cleanDesc}</Text>
                {existingOwner && (
                  <Text size="xs" c="dimmed" mt={4}>
                    Current owner: {existingOwner.lastName}, {existingOwner.firstName}
                  </Text>
                )}
              </Paper>

              <Select
                label="Return to Owner"
                placeholder="Search individuals..."
                data={individualOptions}
                value={reuniteOwnerId || (reuniteItem.individualId || null)}
                onChange={v => setReuniteOwnerId(v || '')}
                searchable
                clearable
              />

              <Textarea
                label="Reunite Notes"
                placeholder="Any notes about the return..."
                value={reuniteNotes}
                onChange={e => setReuniteNotes(e.target.value)}
                minRows={2}
              />

              <Checkbox
                label="Owner refused return / unclaimed"
                checked={reuniteRefused}
                onChange={e => setReuniteRefused(e.currentTarget.checked)}
              />

              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setReuniteItem(null)}>Cancel</Button>
                <Button
                  leftSection={<IconCheck size={14} />}
                  color="var(--color-success)"
                  onClick={handleReunite}
                  loading={reuniting}
                >
                  Confirm Reunite
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>
    </div>
  );
}
